import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import API from '../api';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    client: '', dueDate: '', status: 'unpaid',
    items: [{ description: '', quantity: 1, price: 0 }]
  });

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, []);

  const fetchInvoices = async () => {
    try {
      const { data } = await API.get('/invoices');
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices');
    }
  };

  const fetchClients = async () => {
    try {
      const { data } = await API.get('/clients');
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { description: '', quantity: 1, price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    setForm({ ...form, items: newItems });
  };

  const removeItem = (index) => {
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems });
  };

  const getTotal = () => {
    return form.items.reduce((sum, item) =>
      sum + (item.quantity * item.price), 0
    ).toFixed(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await API.post('/invoices', form);
      setShowModal(false);
      setForm({
        client: '', dueDate: '', status: 'unpaid',
        items: [{ description: '', quantity: 1, price: 0 }]
      });
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create invoice');
    }
    setLoading(false);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await API.put(`/invoices/${id}`, { status });
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this invoice?')) {
      try {
        await API.delete(`/invoices/${id}`);
        fetchInvoices();
      } catch (error) {
        console.error('Error deleting invoice');
      }
    }
  };

  const statusColor = {
    'unpaid': { bg: '#fef3c7', color: '#d97706' },
    'paid': { bg: '#d1fae5', color: '#065f46' },
    'overdue': { bg: '#fee2e2', color: '#991b1b' }
  };

  return (
    <AdminLayout>
      <div style={styles.header}>
        <div>
          <h1 style={styles.heading}>Invoices</h1>
          <p style={styles.subheading}>Manage billing and payments</p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowModal(true)}>
          ➕ New Invoice
        </button>
      </div>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <p style={styles.summaryLabel}>Total Invoices</p>
          <h2 style={styles.summaryValue}>{invoices.length}</h2>
        </div>
        <div style={styles.summaryCard}>
          <p style={styles.summaryLabel}>Paid</p>
          <h2 style={{ ...styles.summaryValue, color: '#10b981' }}>
            {invoices.filter(i => i.status === 'paid').length}
          </h2>
        </div>
        <div style={styles.summaryCard}>
          <p style={styles.summaryLabel}>Unpaid</p>
          <h2 style={{ ...styles.summaryValue, color: '#f59e0b' }}>
            {invoices.filter(i => i.status === 'unpaid').length}
          </h2>
        </div>
        <div style={styles.summaryCard}>
          <p style={styles.summaryLabel}>Total Revenue</p>
          <h2 style={{ ...styles.summaryValue, color: '#4f46e5' }}>
            ${invoices.filter(i => i.status === 'paid')
              .reduce((sum, i) => sum + i.total, 0).toFixed(2)}
          </h2>
        </div>
      </div>

      {/* Invoices Table */}
      <div style={styles.tableContainer}>
        {invoices.length === 0 ? (
          <div style={styles.empty}>No invoices yet. Create your first invoice!</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Invoice #</th>
                <th style={styles.th}>Client</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Due Date</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice._id} style={styles.tableRow}>
                  <td style={styles.td}>
                    <strong>{invoice.invoiceNumber}</strong>
                  </td>
                  <td style={styles.td}>{invoice.client?.name}</td>
                  <td style={styles.td}>
                    <strong>${invoice.total?.toFixed(2)}</strong>
                  </td>
                  <td style={styles.td}>
                    {invoice.dueDate
                      ? new Date(invoice.dueDate).toLocaleDateString()
                      : '—'}
                  </td>
                  <td style={styles.td}>
                    <select
                      value={invoice.status}
                      onChange={(e) =>
                        handleStatusChange(invoice._id, e.target.value)
                      }
                      style={{
                        ...styles.statusSelect,
                        backgroundColor: statusColor[invoice.status].bg,
                        color: statusColor[invoice.status].color
                      }}
                    >
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </td>
                  <td style={styles.td}>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDelete(invoice._id)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Create New Invoice</h2>
            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Client *</label>
              <select
                style={styles.input}
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
              >
                <option value="">Select a client</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Due Date</label>
              <input
                style={styles.input}
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              />
            </div>

            {/* Invoice Items */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Items</label>
              {form.items.map((item, index) => (
                <div key={index} style={styles.itemRow}>
                  <input
                    style={{ ...styles.input, flex: 3 }}
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) =>
                      updateItem(index, 'description', e.target.value)
                    }
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) =>
                      updateItem(index, 'quantity', parseInt(e.target.value))
                    }
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="number"
                    placeholder="Price"
                    value={item.price}
                    onChange={(e) =>
                      updateItem(index, 'price', parseFloat(e.target.value))
                    }
                  />
                  {form.items.length > 1 && (
                    <button
                      style={styles.removeBtn}
                      onClick={() => removeItem(index)}
                    >✕</button>
                  )}
                </div>
              ))}
              <button style={styles.addItemBtn} onClick={addItem}>
                + Add Item
              </button>
            </div>

            <div style={styles.totalRow}>
              <strong>Total: ${getTotal()}</strong>
            </div>

            <div style={styles.modalButtons}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowModal(false)}
              >Cancel</button>
              <button
                style={styles.submitBtn}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const styles = {
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '30px'
  },
  heading: { margin: '0', fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  subheading: { margin: '4px 0 0 0', color: '#718096', fontSize: '15px' },
  addBtn: {
    padding: '12px 24px', backgroundColor: '#4f46e5', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600'
  },
  summaryGrid: {
    display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap'
  },
  summaryCard: {
    backgroundColor: 'white', borderRadius: '12px', padding: '20px',
    flex: 1, minWidth: '150px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
  },
  summaryLabel: { margin: '0 0 8px 0', color: '#718096', fontSize: '14px' },
  summaryValue: { margin: '0', fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  tableContainer: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)', overflow: 'hidden'
  },
  empty: { padding: '60px', textAlign: 'center', color: '#718096' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f7f8fc' },
  th: {
    padding: '14px 20px', textAlign: 'left', fontSize: '13px',
    fontWeight: '600', color: '#718096', borderBottom: '1px solid #e2e8f0'
  },
  tableRow: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '14px 20px', fontSize: '14px', color: '#4a5568' },
  statusSelect: {
    padding: '4px 8px', borderRadius: '20px', border: 'none',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer'
  },
  deleteBtn: {
    padding: '6px 12px', backgroundColor: '#fee2e2', color: '#dc2626',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000
  },
  modal: {
    backgroundColor: 'white', borderRadius: '12px', padding: '32px',
    width: '100%', maxWidth: '560px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    maxHeight: '90vh', overflowY: 'auto'
  },
  modalTitle: { margin: '0 0 24px 0', fontSize: '22px', fontWeight: '700', color: '#1a1a2e' },
  error: {
    backgroundColor: '#fee2e2', color: '#dc2626', padding: '10px',
    borderRadius: '8px', marginBottom: '16px', fontSize: '14px'
  },
  inputGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#333' },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
  },
  itemRow: { display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' },
  removeBtn: {
    padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700'
  },
  addItemBtn: {
    padding: '8px 16px', backgroundColor: '#f0f4ff', color: '#4f46e5',
    border: '1px dashed #4f46e5', borderRadius: '6px', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600', marginTop: '4px'
  },
  totalRow: {
    textAlign: 'right', fontSize: '18px', color: '#1a1a2e',
    padding: '12px 0', borderTop: '1px solid #e2e8f0', marginBottom: '8px'
  },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '16px' },
  cancelBtn: {
    flex: 1, padding: '12px', backgroundColor: '#f7f8fc', color: '#4a5568',
    border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600'
  },
  submitBtn: {
    flex: 1, padding: '12px', backgroundColor: '#4f46e5', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600'
  }
};

export default Invoices;